import { Autocomplete, Box, Checkbox, Chip, TextField, Typography } from "@mui/material";
import type { PeriodRowOption } from "../utils/statementColumns.ts";

const PLACEHOLDER = "Pick rows";
const slotProps = { listbox: { sx: { maxHeight: 240 } } };

interface Props {
  options: PeriodRowOption[];
  selectedGuids: string[];
  readonly: boolean;
  onChange: (guids: string[]) => void;
}

const PeriodRowsAutocomplete = ({ options, selectedGuids, readonly, onChange }: Props) => {
  const value = options.filter((option) => selectedGuids.includes(option.guid));

  const handleChange = (selected: PeriodRowOption[]) => {
    const guids = new Set(selected.map((option) => option.guid));
    onChange(options.filter((option) => guids.has(option.guid)).map((option) => option.guid));
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      slotProps={slotProps}
      options={options}
      value={value}
      disabled={readonly}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.guid}
      isOptionEqualToValue={(option, selected) => option.guid === selected.guid}
      onChange={(_, selected) => handleChange(selected)}
      renderInput={(params) => <TextField {...params} placeholder={value.length === 0 ? PLACEHOLDER : undefined} />}
      renderValue={(selected, getItemProps) =>
        selected.map((option, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return <Chip key={key} variant="outlined" label={option.label} {...itemProps} />;
        })
      }
      renderOption={({ key, ...props }, option, { selected }) => (
        <Box component="li" key={key} {...props}>
          <Checkbox checked={selected} tabIndex={-1} sx={{ p: 0, mr: 1, ml: option.depth * 2 }} />
          <Typography noWrap>{option.label}</Typography>
        </Box>
      )}
    />
  );
};

export default PeriodRowsAutocomplete;
